import React from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Stack,
} from "@mui/material";
import QrCodeIcon from "@mui/icons-material/QrCode2";

const formatNumber = (val) =>
  Number(val || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const FiscalInvoiceDetailsTab = ({ invoice }) => {
  const { header, fiscalData } = invoice;

  return (
    <Grid container spacing={3}>
      {/* LEFT COLUMN: Cryptography & Company */}
      <Grid item xs={12} md={4}>
        <Stack spacing={3}>
          <Card
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "error.main",
              bgcolor: "background.paper",
            }}
          >
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <QrCodeIcon color="error" />
                <Typography variant="h6" fontWeight="bold">
                  eTIMS Verification
                </Typography>
              </Stack>
              <Divider sx={{ mb: 2 }} />

              <Typography variant="caption" color="text.secondary">
                CU Serial Number (SDC ID)
              </Typography>
              <Typography
                variant="body2"
                fontWeight="bold"
                sx={{ fontFamily: "monospace" }}
                gutterBottom
              >
                {fiscalData.sdcId}
              </Typography>

              <Typography variant="caption" color="text.secondary" mt={1}>
                Digital Signature
              </Typography>
              <Typography
                variant="body2"
                fontWeight="bold"
                sx={{ fontFamily: "monospace" }}
                gutterBottom
              >
                {fiscalData.signature}
              </Typography>

              <Typography variant="caption" color="text.secondary" mt={1}>
                Internal Data
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                gutterBottom
              >
                {fiscalData.internalData}
              </Typography>

              <Typography variant="caption" color="text.secondary" mt={1}>
                QR Code Verification URL
              </Typography>
              <Typography
                variant="body2"
                color="primary.main"
                sx={{ wordBreak: "break-all" }}
              >
                <a
                  href={fiscalData.qrCodeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "inherit" }}
                >
                  {fiscalData.qrCodeUrl}
                </a>
              </Typography>
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Issued By (Registered Trader)
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle1" fontWeight="bold">
                {header?.companySnapshot?.companyName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                PIN: {header?.companySnapshot?.taxId}
              </Typography>
            </CardContent>
          </Card>
        </Stack>
      </Grid>

      {/* RIGHT COLUMN: Items & Tax Summary */}
      <Grid item xs={12} md={8}>
        <Card
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            mb: 3,
            bgcolor: "background.paper",
          }}
        >
          <CardContent>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Fiscalized Line Items
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: "background.default" }}>
                  <TableRow>
                    <TableCell>Description</TableCell>
                    <TableCell>eTIMS Code</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Unit Price</TableCell>
                    <TableCell align="right">Tax Amount</TableCell>
                    <TableCell align="right">Line Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fiscalData.invoiceItems?.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {item.itemName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Tax: {item.taxTypeCode}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="caption"
                          sx={{ fontFamily: "monospace" }}
                        >
                          {item.itemCode}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {item.quantity} {item.qtyUnitCode}
                      </TableCell>
                      <TableCell align="right">
                        {formatNumber(item.unitPrice)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: "text.secondary" }}>
                        {formatNumber(item.taxAmount)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>
                        {formatNumber(item.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        <Card
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.default",
          }}
        >
          <CardContent>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              KRA Tax Breakdown & Totals
            </Typography>
            <Grid container spacing={4}>
              <Grid item xs={12} sm={6}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  mb={1}
                  mt={1}
                >
                  <Typography variant="body2" color="text.secondary">
                    Taxable A (Exempt)
                  </Typography>
                  <Typography variant="body2" fontWeight="500">
                    {formatNumber(fiscalData.taxableA)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="text.secondary">
                    Taxable B (16%)
                  </Typography>
                  <Typography variant="body2" fontWeight="500">
                    {formatNumber(fiscalData.taxableB)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="text.secondary">
                    Taxable C (0%)
                  </Typography>
                  <Typography variant="body2" fontWeight="500">
                    {formatNumber(fiscalData.taxableC)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="text.secondary">
                    Taxable D (Non-Vatable)
                  </Typography>
                  <Typography variant="body2" fontWeight="500">
                    {formatNumber(fiscalData.taxableD)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="text.secondary">
                    Taxable E (8%)
                  </Typography>
                  <Typography variant="body2" fontWeight="500">
                    {formatNumber(fiscalData.taxableE)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  mb={1}
                  mt={1}
                >
                  <Typography variant="body2" color="text.secondary">
                    Total Taxable Amount
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formatNumber(fiscalData.totalTaxableAmount)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="text.secondary">
                    Total Tax Amount
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formatNumber(fiscalData.totalTaxAmount)}
                  </Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography variant="h6" fontWeight="bold">
                    Grand Total
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color="secondary.main"
                  >
                    {fiscalData.currency}{" "}
                    {formatNumber(fiscalData.totalInvoiceAmount)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};
export default FiscalInvoiceDetailsTab;
